import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from './client.ts';
import { qk } from './keys.ts';
import type {
  Playlist,
  PlaylistWithItems,
  CreatePlaylist,
  UpdatePlaylist,
  SetPlaylistItems,
} from '@seencast/shared';
import type { ListEnvelope } from './client.ts';

export function listPlaylists(): Promise<Playlist[]> {
  return request<ListEnvelope<Playlist>>('/playlists').then((r) => r.items);
}

export function usePlaylists() {
  return useQuery({ queryKey: qk.playlists, queryFn: listPlaylists });
}

// GAP: docs/03 lists GET /v1/playlists but not the single-playlist shape;
// we assume GET /v1/playlists/:id returns the playlist with its ordered items.
export function getPlaylist(id: string): Promise<PlaylistWithItems> {
  return request<PlaylistWithItems>(`/playlists/${id}`);
}

export function usePlaylist(id: string | undefined) {
  return useQuery({
    queryKey: qk.playlist(id ?? ''),
    queryFn: () => getPlaylist(id as string),
    enabled: !!id,
  });
}

export function useCreatePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePlaylist) => request<Playlist>('/playlists', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.playlists }),
  });
}

export function useUpdatePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePlaylist }) =>
      request<Playlist>(`/playlists/${id}`, { method: 'PATCH', body }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: qk.playlists });
      qc.invalidateQueries({ queryKey: qk.playlist(v.id) });
    },
  });
}

export function useDeletePlaylist() {
  const qc = useQueryClient();
  return useMutation({
    // 409 if referenced by a schedule.
    mutationFn: (id: string) => request<void>(`/playlists/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.playlists }),
  });
}

export function useSetPlaylistItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SetPlaylistItems }) =>
      request<PlaylistWithItems>(`/playlists/${id}/items`, { method: 'PUT', body }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: qk.playlist(v.id) });
      qc.invalidateQueries({ queryKey: qk.playlists });
    },
  });
}

// --- Bulk: add media to many playlists at once (S4 bulk ops) --------------------
// POST /v1/playlists/bulk-add appends each selected media item to the END of every
// target playlist. ALL-OR-NOTHING (architect contract): any unknown/foreign id — or
// a media not yet status=ready — → 422 and nothing is applied. Request arrays are
// deduped (same id twice = once); there is NO dedupe against existing playlist
// contents (repeats are allowed), so `added` is always the (deduped) media count.
// The 422 path only fires if a selected item was deleted/unreadied concurrently
// (ids come from our own grid) — the UI surfaces it as a clear toast, not silently.
export interface BulkAddToPlaylists {
  media_ids: string[];
  playlist_ids: string[];
}
export interface BulkAddToPlaylistsResult {
  results: Array<{ playlist_id: string; added: number }>;
}

export function bulkAddMediaToPlaylists(
  body: BulkAddToPlaylists,
): Promise<BulkAddToPlaylistsResult> {
  return request<BulkAddToPlaylistsResult>('/playlists/bulk-add', { method: 'POST', body });
}

export function useBulkAddToPlaylists() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: bulkAddMediaToPlaylists,
    // Item counts + individual playlist contents change; refresh both.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['playlists'] }),
  });
}
